import page from "router/page";
import _ from "lodash";
import moment from "moment";

import Report from "report/report";
import ActivityProcessor from "utils/activity-processor"; 
import DateRangeExtractor from "utils/date-range-extractor";
import Formatter from "utils/formatter";
import tokens from "utils/tokens";

const d3 = require("d3");

const activityProcessor   = new ActivityProcessor();
const dateRangeExtractor  = new DateRangeExtractor();
const formatter           = new Formatter();

/**
 * Handle the display of activity/resource report
 */
class ResourceActivityReport extends Report {

    name = 'resource';
    title = 'Activities by Project';

    /**
     * Register routes that this report handles
     */
    registerRoutes() {
        let app = this.app;

        page('/activity/resource', (context) => {
            this.setReport(context);
            this.assemble();
        });
    
        page('/activity/resource/:resource/:from/:to', async (context) => {            
            let resource   = context.params.resource;
            let dateFrom   = context.params.from;
            let dateTo     = context.params.to;

            this.setReport(context);

            console.log(`Generating report for ${resource} – Report Task by Resource`);
            await this.generateReportTaskByResource(dateFrom, dateTo, resource);
    
            this.assemble();
            app.findComponent('DateRangePicker').setDate(dateFrom, dateTo);
        
            this.drawChart();
        });
    }
    
    /**
     * Reduce the timeplot values into a serial aggregates
     * Return an array of aggregated time spent.
     * 
     * @param {array} timePlot - Time plot object
     * 
     * @return {array}
     */
    aggregateTimeByType(timePlot) {
        
        let hoursSpend = [];

        _.each(timePlot, (billable, i) => {

            let timePerDay = billable.datePlot.values;

            if (i === 0) {
                hoursSpend = timePerDay;
                return;
            }

            // merge thhe values
            _.reduce(timePerDay, (hoursSpend, time, i) => {
                hoursSpend[i] += time;

                return hoursSpend;
            }, hoursSpend);
        });

        return hoursSpend;
    }

    /**
     * Prepare the data layer for chart
     * 
     * @returns {Object}
     */
    prepareDataLayer() {
        let projectsWorkedOn = this.app.get('projectsWorkedOn');

        // find all billable
        let billables = _.filter(projectsWorkedOn, {type: 'billable'});
        let nonBillables = _.filter(projectsWorkedOn, {type: 'non-billable'});

        // reduce them into single values / day
        let utilizedHours    = [];
        let billableHours    = this.aggregateTimeByType(billables);
        let nonBillableHours = this.aggregateTimeByType(nonBillables);

        _.reduce(billableHours, (utilizedHours, value, i) => {
            let productivity = nonBillableHours[i] + value;
            let util = 0;

            if(productivity > 0) {
                util = value / productivity;
            }

            utilizedHours.push(util);

            return utilizedHours;
        }, utilizedHours);

        return {billableHours, nonBillableHours, utilizedHours};
    }

    /**
     * Draw the utilization and productivity chart
     * 
     * @return {void}
     */
    drawChart() {

        let app        = this.app;
        let dates      = app.get('dateMarkings.dates');
        
        let days       = []
        let data       = [];

        let {billableHours, nonBillableHours, utilizedHours} = this.prepareDataLayer();

        _.each(dates, (date, i) => {
            let day         = formatter.date.showDay(date);
            let utilization = utilizedHours[i],
                billable    = billableHours[i],
                nonBillable = nonBillableHours[i];
            
            data.push({
                day          : date,
                value        : utilization,
                productivity : billable + nonBillable,
                billable     : billable,
                label        : formatter.toPercentage(utilization)
            });

            days.push(day);
        });


        // Average utilization
        app.set(
            'averageUtilization',
            formatter.toPercentage(
                d3.mean(
                    _.filter(_.map(data, 'value'), v => { return v > 0; })
                )
            )
        );

        app.set(
            'averageBillableHours',
            formatter.formatHours(
                d3.mean(
                    _.filter(_.map(data, 'billable'), v => { return v > 0; })
                )
            )
        );

        app.set(
            'averageProductivityHours',
            formatter.formatHours(
                d3.mean(
                    _.filter(_.map(data, 'productivity'), v => { return v > 0; })
                )
            )
        );

        // ----------------------------------------------------------------
        
        let $utilizationChart  = d3.select('#utilization-chart');
        let $productivityChart = d3.select('#productivity-chart');

        // Exit if no data is generated
        if (data.length === 0) {
            return;
        }

        let width             = parseInt($utilizationChart.style('width'), 10);
        let height            = 376;

        // ----------------------------------------------------------------
        // Benchmark hours
        const benchmarkHours = 8;
        
        let margin = ({
            top    : tokens.space('quad'), 
            right  : 0,
            bottom : tokens.space('quad'), 
            left   : 0
        });

        // ----------------------------------------------------------------

        let x = d3.scaleTime()
            .domain(d3.extent(data, d => d.day))
            .range([margin.left, width - margin.right]);

        let minH            = 10;
        let maxProductivity = _.max(_.map(data, 'productivity'));

        let y = d3.scaleLinear()
        // .domain(d3.extent(data, d => d.productivity))
            .domain([0, (maxProductivity > minH) ? maxProductivity : minH]).nice()
            .range([height - margin.bottom, margin.top]);

        let xAxis = g => {
            g.attr("transform", `translate(0,${height - margin.bottom})`)
            .call(d3.axisBottom(x)
                // .tickFormat(i => `${data[i].day}`)
                .tickSizeOuter(0))
        };

        let addUtilizationBar = (chart, lowerTreshold, upperTreshold) => {
            chart.append('rect')
                .attr('class', 'billable-band')
                .attr('width', width)
                .attr('height', y(lowerTreshold) - y(upperTreshold))  // 80% billables
                .attr('fill', '#00ACF4')
                .attr('fill-opacity', 0.1)
                .attr('x', 0)
                .attr('y', y(upperTreshold));

            chart.append("line")
                .attr("stroke", '#00ACF4')
                .attr('stroke-width', 1.5)
                .attr("stroke-dasharray", ("6, 6"))
                .attr("x1", 0)
                .attr("y1", y(benchmarkHours))
                .attr("x2", width)
                .attr("y2", y(benchmarkHours));

            chart.append('text')
                .text(`${upperTreshold}h`)
                .attr('font-size', '10px')
                .attr('font-weight', 'bold')
                .attr('fill', tokens.token().color['darker-grey'])
                .attr('x', 6)
                .attr('y', y(upperTreshold) - 8);

            chart.append('text')
                .text(`${lowerTreshold}h`)
                .attr('font-size', '10px')
                .attr('fill', tokens.token().color['darker-grey'])
                .attr('x', 6)
                .attr('y', y(lowerTreshold) + 14);
        }

        // ----------------------------------------------------------------
        
        let utilizationChart  = () => {

            // creates the main chart container
            const chart = $utilizationChart
                .append('svg')
                .attr("viewBox", [0, 0, width, height]);

            // Add the X group axis ticks
            chart
                .append("g")
                .attr("class", "chart-axis")
                .call(xAxis);
            
            chart
                .append('text')
                .attr('x', 0)
                .attr('y', 18)
                .attr('font-size', '14px')
                .attr('font-weight', 'bold')
                .attr("fill", tokens.token().color['darker-grey'])
                .attr("text-anchor", "start")
                .text('Resource Utilization');

            // ----------------------------------------------------------------
            // Utilization plot
            let plotUtilization = d3.area()
                .curve(d3.curveStepAfter)
                .x((d, i) => x(d.day))
                .y0(y(0))
                .y1(d => y(d.billable));

            chart.append('g')
                .attr('id', 'utilization-plot')
                .append('path')
                .attr('class', 'utilization-line')
                .attr('d', plotUtilization(data))
                .attr('fill', '#1D3893')
                .attr('fill-opacity', 0.1)
                .attr('stroke-width', 1.5)
                .attr('stroke', '#1D3893');

            let lowerTreshold = benchmarkHours * .8;
            addUtilizationBar(chart, lowerTreshold, 8);

            return chart.node();
        };

        let productivityChart = () => {
            // creates the main chart container
            const chart = $productivityChart
                .append('svg')
                .attr("viewBox", [0, 0, width, height]);

            // Add the X group axis ticks
            chart
                .append("g")
                .attr("class", "chart-axis")
                .call(xAxis);
            
            chart
                .append('text')
                .attr('x', 0)
                .attr('y', 18)
                .attr('font-size', '14px')
                .attr('font-weight', 'bold')
                .attr("fill", tokens.token().color['darker-grey'])
                .attr("text-anchor", "start")
                .text('Resource Productivity');

            // ----------------------------------------------------------------
            // Utilization plot
            let plotProductivity = d3.area()
                .curve(d3.curveStepAfter)
                .x((d, i) => x(d.day))
                .y0(y(0))
                .y1(d => y(d.productivity));
            
            chart.append('g')
                .attr('id', 'utilization-plot')
                .append('path')
                .attr('class', 'utilization-line')
                .attr('d', plotProductivity(data))
                .attr('fill', '#111B54')
                .attr('fill-opacity', 0.1)
                .attr('stroke-width', 1.5)
                .attr('stroke', '#111B54');

            // These numbers are from the capacity plan
            const lowerTreshold  = benchmarkHours * .9;
            addUtilizationBar(chart,  lowerTreshold,  benchmarkHours);

            return chart.node();
        }

        utilizationChart();
        productivityChart();

    }

    /**
     * Assemble the components post request
     */
    assemble() {
        let app              = this.app;
        let resourceSelector = app.findComponent('ResourceSelector');
        let timeline         = app.findComponent('TimelineBreakdown');
        // let datePicker       = app.findComponent('DateRangePicker');
    
        let dates            = app.get('dateMarkings.dates');
        let user             = app.get('active.user');

        resourceSelector.apply();

        // console.log(datePicker.setDate);
        
        if (user) { resourceSelector.o.setValue(user.bindname); }

        if (!app.hasRecords()) { return; }

        timeline.refit(dates);
    }

    /**
     * Build the report's data layer
     * 
     * @param {string} dateFrom - Date range start
     * @param {string} dateTo   - Date range end
     * @param {string} bindname - User Bindname
     */
    async generateReportTaskByResource(dateFrom, dateTo, bindname) {

        let app                 = this.app;
        let resources           = app.get('resources.lookup');

        let response            = await this.apiCaller.report.getTaskByResource(dateFrom, dateTo, bindname);

        app.set('active.user', resources[bindname]);

        app.reportIsLoaded();
        app.hasRecords(response);

        if (!app.hasRecords()) { return; }

        let report              = activityProcessor.processActivitiesOfResource(response, dateFrom, dateTo);

        let workDays            = dateRangeExtractor.getWeekdays(dateFrom, dateTo);
        let dateMarkings        = dateRangeExtractor.addMarkings(workDays);

        let totalBillables      = (report.aggregatedSummary['billable']) ? report.aggregatedSummary['billable'].timeSpent.hours : 0,
            totalNonBillables   = (report.aggregatedSummary['non-billable']) ? report.aggregatedSummary['non-billable'].timeSpent.hours : 0,
            totalNonProductive  = (report.aggregatedSummary['non-productive']) ? report.aggregatedSummary['non-productive'].timeSpent.hours : 0;

        let totalTime           = totalBillables + totalNonBillables + totalNonProductive;
        let totalProductiveTime = totalBillables + totalNonBillables;

        let projects            = _.orderBy(report.group,'timeSpent.seconds', 'desc');
        
        
        app.set('dateMarkings', dateMarkings);
        
        app.set('aggregates.totalBillableHours', totalBillables);
        app.set('aggregates.totalNonBillableHours', totalNonBillables);
        app.set('aggregates.totalNonProductiveHours', totalNonProductive);

        app.set('distribution.billable',      formatter.toPercentage(totalBillables / totalTime));
        app.set('distribution.nonBillable',   formatter.toPercentage(totalNonBillables / totalTime));
        app.set('distribution.nonProductive', formatter.toPercentage(totalNonProductive / totalTime));

        app.set('distribution.billableLabel',      formatter.toPercentage(totalBillables / totalProductiveTime));
        app.set('distribution.nonBillableLabel',   formatter.toPercentage(totalNonBillables / totalProductiveTime));
        
        app.set('overall.timeSpent', report.timeSpent.hours);

        app.set('projectsWorkedOn', projects);
        app.set('plottedActivities', report.plottedActivities);
    }

}

module.exports = ResourceActivityReport;
